import { t, type ErrorCodeMapperEntry } from "./types.js";

/** Error codes 1000-1999. One thousand-block per file: a duplicate number here can only
 *  collide within this file, which is what makes the split safer than one shared object. */
export const legacy1000Errors = {
  // ==========================================================================================
  // Count-driven plural entries below declare `errorMessagePlural` alongside the singular
  // `errorMessage`. This is NOT `{{param}}` template interpolation — the two strings are
  // unrelated sentences (e.g. "This unit is in use..." vs "One or more of the selected units are
  // in use..."), so a single templated message can't express it. `getErrorMessageTemplate` below
  // picks between them from a `count` param. `dynamicParams: ["count"]` still documents that this
  // entry needs a `count` value from the caller, same as any other dynamic entry.
  // ==========================================================================================
  LICENCE_EXPIRED: {
    errorCode: 1001,
    errorMessage: t("Licence Expired"),
    isDynamic: false,
  },
  // Live at two senders in estimateHelper.service.ts. getEstimateData — the shared authorization
  // gate called at the start of nearly every estimates write operation — sends it to a caller who
  // can view the estimate but lacks the route's permission; one who cannot view it gets
  // ESTIMATE_NOT_FOUND. restoreEstimateVersion sends it itself to an editor without RESOURCE_UPLOAD,
  // since a restore names the current draft first. No client branches on this code: estimate
  // components show its sentence as the toast of a refused write.
  ESTIMATE_NO_ACCESS: {
    errorCode: 1100,
    errorMessage: t("You Don't Have Required Permission to Perform this Action"),
    isDynamic: false,
  },
  // Live at estimates.service.ts's insertEstimateItemInSession. Confirmed safe —
  // EstimateCostDatabaseItemsGridNew.tsx branches on this exact code to track the item for its own
  // "incorrectly placed items" modal, never reads errorMessage.
  ESTIMATE_ITEM_INCORRECT_PLACEMENT: {
    errorCode: 1205,
    errorMessage: t("Please add the item under its Cost Database parent item hierarchy"),
    isDynamic: false,
  },
  // Live at costDatabase.service.ts's deleteCostDatabaseData. No confirmed frontend consumer found.
  COST_DATABASE_ATTACHED_TO_ESTIMATE: {
    errorCode: 1302,
    errorMessage: t("The cost database could not be deleted as it is attached to estimates"),
    isDynamic: false,
  },
  // Live at estimates.service.ts. No confirmed frontend consumer found.
  ESTIMATE_ITEM_PURCHASE_UNIT_NOT_EDITABLE: {
    errorCode: 1110,
    errorMessage: t("This item Purchase Unit can not be edited as it contains quantity from takeoffs"),
    isDynamic: false,
  },
  ESTIMATE_ITEM_MEASUREMENT_UNIT_NOT_EDITABLE: {
    errorCode: 1171,
    errorMessage: t("This item Measurement Unit can not be edited as it contains quantity from takeoffs"),
    isDynamic: false,
  },
  // Live at 2 sites in takeoffs.service.ts (cosmetic wording variants of the same concept,
  // standardized here). No confirmed frontend consumer found for either.
  TAKEOFF_NOT_FOUND: {
    errorCode: 1111,
    errorMessage: t("This takeoff doesn't exist in the estimate's current version."),
    isDynamic: false,
  },
  // Live at measurements.service.ts. No confirmed frontend consumer found.
  MEASUREMENT_TYPE_CHANGE_NOT_ALLOWED: {
    errorCode: 1112,
    errorMessage: t("Editing type is not allowed since the measurement is already a part of certain takeoff(s)"),
    isDynamic: false,
  },
  // Live at measurements.service.ts. No confirmed frontend consumer found.
  MEASUREMENT_NOT_FOUND: {
    errorCode: 1113,
    errorMessage: t("Measurement not found"),
    isDynamic: false,
  },
  ESTIMATE_GETVALIDITEMS_COST_DATABASE_NOT_EXISTS: {
    errorCode: 1102,
    errorMessage: t("This estimate has no linked cost database"),
    isDynamic: false,
  },
  COST_DATABASE_PARENT_ITEM_NOT_FOUND: {
    errorCode: 1103,
    errorMessage: t("Parent item not found in the cost database"),
    isDynamic: false,
  },
  // BILLING_END: {
  //   errorCode: errorCodes.BILLING_END,
  //   errorMessage: "Billing End will end on {{year}} year",
  //   isDynamic: true,
  //   dynamicParams: ["year"],
  // },
  MEASUREMENT_UNIT_IN_USE: {
    errorCode: 1401,
    errorMessage: t("This unit is in use hence can't be deleted."),
    errorMessagePlural: t("One or more of the selected units are in use hence can't be deleted."),
    isDynamic: true,
    dynamicParams: ["count"],
  },
  MEASUREMENT_UNIT_NOT_DELETABLE: {
    errorCode: 1402,
    errorMessage: t("This is a default unit hence can't be deleted."),
    errorMessagePlural: t("One or more of the selected units are default units hence can't be deleted."),
    isDynamic: true,
    dynamicParams: ["count"],
  },
  // 1405-1415 is the units module's SOMETHING_WENT_WRONG (9000) / RESOURCE_NOT_FOUND (9003) split,
  // continuing the 14xx units sub-block above. Nine `noPermissionMsg` sites moved to
  // INSUFFICIENT_PERMISSION (7012) instead — the module already paired that exact message with that
  // exact code at two other sites — and one site reused MEASUREMENT_UNIT_NOT_FOUND (1125) verbatim.
  // 1405-1415 — the units module's generic-code split. Every message is copied verbatim from the
  // call site it replaces.
  UNIT_NAME_REQUIRED: {
    errorCode: 1405,
    errorMessage: t("Name is required"),
    isDynamic: false,
  },
  UNIT_SYMBOL_NOT_IN_UNIT: {
    errorCode: 1406,
    errorMessage: t("Selected symbol must be one of the unit's symbols."),
    isDynamic: false,
  },
  MEASUREMENT_UNIT_TYPE_INVALID: {
    errorCode: 1407,
    errorMessage: t("Invalid measurement unit type."),
    isDynamic: false,
  },
  MEASUREMENT_UNIT_LOCKED: {
    errorCode: 1425,
    errorMessage: t(
      "The name, type and factor of this unit can't be changed because it is a default unit or is in use.",
    ),
    isDynamic: false,
  },
  // Rename collides with an existing unit of the same name. Measurement and purchase units get
  // separate codes because their sentences name the unit kind explicitly.
  MEASUREMENT_UNIT_NAME_EXISTS: {
    errorCode: 1408,
    errorMessage: t("Measurement Unit cannot be edited as another unit with this name already exists."),
    isDynamic: false,
  },
  PURCHASE_UNIT_NAME_EXISTS: {
    errorCode: 1409,
    errorMessage: t("Purchase Unit cannot be edited as another unit with this name already exists."),
    isDynamic: false,
  },
  MEASUREMENT_UNIT_NO_DISPLAY_SYMBOL: {
    errorCode: 1410,
    errorMessage: t("The selected measurement unit has no display symbol."),
    isDynamic: false,
  },
  UNIT_CONVERSION_RATE_INVALID: {
    errorCode: 1411,
    errorMessage: t("Invalid conversion rate"),
    isDynamic: false,
  },
  PURCHASE_UNITS_NONE_SPECIFIED_FOR_DELETION: {
    errorCode: 1412,
    errorMessage: t("No purchase units specified for deletion"),
    isDynamic: false,
  },
  UNIT_RESOURCE_ID_REQUIRED: {
    errorCode: 1413,
    errorMessage: t("resourceId is required"),
    isDynamic: false,
  },
  UNIT_ESTIMATE_ID_REQUIRED: {
    errorCode: 1414,
    errorMessage: t("estimateId is required"),
    isDynamic: false,
  },
  // Replaces `unitNotFoundMessage(count)` (units.constant.ts), which was only ever a singular/plural
  // switch — not a runtime interpolation. That is `errorMessagePlural` territory, the same shape
  // MEASUREMENT_UNIT_IN_USE (1401) already uses, so these two sites did NOT need the
  // dynamic-message deferral the rest of the migration applies to interpolated text.
  // Both forms are `unitNotFoundMessage`'s own two branches, verbatim.
  UNIT_NOT_FOUND: {
    errorCode: 1415,
    errorMessage: t("The selected unit could not be found."),
    errorMessagePlural: t("One or more of the selected units could not be found."),
    isDynamic: true,
    dynamicParams: ["count"],
  },
  PURCHASE_UNIT_IN_USE: {
    errorCode: 1403,
    errorMessage: t("This unit is in use hence can't be deleted."),
    errorMessagePlural: t("One or more of the selected units are in use hence can't be deleted."),
    isDynamic: true,
    dynamicParams: ["count"],
  },
  PURCHASE_UNIT_NOT_DELETABLE: {
    errorCode: 1404,
    errorMessage: t("This is a default unit hence can't be deleted."),
    errorMessagePlural: t("One or more of the selected units are default units hence can't be deleted."),
    isDynamic: true,
    dynamicParams: ["count"],
  },
  // 1501-1502 arrived from staging's currency archive/delete guards, which previously rode
  // `status: true` bodies carrying a `CannotBeArchived` marker and are now ordinary refusals.
  //
  // ⚠️ CURRENCY_DEFAULT_ARCHIVE is deliberately ONE code for two call sites in
  // currency.mongo.service.ts — the archive guard in `setCurrencyRecordStatus` and the delete guard in
  // `deleteCurrencyData`. Staging worded them separately ("cannot be archived" / "cannot be
  // deleted"); web branches on the code alone in BOTH handlers (currencies.tsx) and words its own
  // toast from `t()` keys, so splitting the code would buy nothing and break those two branches.
  // The single message below therefore has to cover both verbs.
  CURRENCY_DEFAULT_ARCHIVE: {
    errorCode: 1501,
    errorMessage: t('"{{name}}" is the portal default currency and cannot be archived or deleted.'),
    isDynamic: true,
    dynamicParams: ["name"],
  },
  // Singular/plural, not interpolation — the two sentences differ in more than a number, which is
  // exactly what `errorMessagePlural` exists for (see MEASUREMENT_UNIT_IN_USE above). Both forms are
  // staging's own branches, verbatim.
  CURRENCY_IN_USE: {
    errorCode: 1502,
    errorMessage: t("This currency cannot be archived — a project or a cost database still uses it."),
    errorMessagePlural: t("These currencies cannot be archived — a project or a cost database still uses them."),
    isDynamic: true,
    dynamicParams: ["count"],
  },
  // 1101 and 1204 predate this migration and had NO entry at all, so both resolved to a blank
  // message. Their call sites all still carry their own inline text, so these entries are the
  // fallback for a code-driven client rather than the primary source.
  ESTIMATE_NOT_FOUND: {
    errorCode: 1101,
    errorMessage: t("Estimate not found!"),
    isDynamic: false,
  },
  // 1114+ is the estimates module's RESOURCE_NOT_FOUND (9003) split. Only ONE site in the whole module
  // turned out to be a wording-identical reuse of an existing code (SPACE_NOT_FOUND, 7015); the codes
  // below each carry a sentence no existing code had. Note that ESTIMATE_NOT_FOUND (1101) and
  // ESTIMATE_ITEM_INVALID (1204) were NOT reusable here despite the obvious names: neither has a
  // mapper entry at all, so both currently resolve to a blank message.
  //
  // An `EstimateItems` row does not exist in the resolved estimate version. 4 call sites — including
  // one that fires when `itemId` is absent from the payload, which ships the same sentence today.
  ESTIMATE_ITEM_NOT_FOUND: {
    errorCode: 1114,
    errorMessage: t("Item not found"),
    isDynamic: false,
  },
  // `syncEstimateItemMeasurementUnit`: the item row is missing OR exists without a `code`. Its own
  // sentence ("Estimate item not found.") differs from ESTIMATE_ITEM_NOT_FOUND's, so it keeps its own
  // code rather than being re-worded.
  ESTIMATE_ITEM_NOT_FOUND_OR_UNCODED: {
    errorCode: 1115,
    errorMessage: t("Estimate item not found."),
    isDynamic: false,
  },
  // `getValidItems`: the parent `EstimateItems` row is absent from the current estimate version.
  // Deliberately not COST_DATABASE_PARENT_ITEM_NOT_FOUND (1103) — that names the cost database.
  ESTIMATE_PARENT_ITEM_NOT_FOUND: {
    errorCode: 1116,
    errorMessage: t("Parent item not found in the current estimate version"),
    isDynamic: false,
  },
  // An `EstimateVersions` row could not be resolved. 3 call sites. Two of them scope the lookup by
  // `estimateId` on purpose, so a caller holding MODIFY on one estimate cannot reach another
  // estimate's version by guessing a versionNumber — the not-found answer is the mask, keep it.
  ESTIMATE_VERSION_NOT_FOUND: {
    errorCode: 1117,
    errorMessage: t("Estimate version not found"),
    isDynamic: false,
  },
  // `restoreEstimateVersion`: the version being restored could not be resolved.
  ESTIMATE_VERSION_TO_RESTORE_NOT_FOUND: {
    errorCode: 1118,
    errorMessage: t("The estimate version to restore could not be found."),
    isDynamic: false,
  },
  // A `CostDatabases` row (or its version data) could not be resolved. 3 call sites. One is an
  // explicit cross-portal OWNERSHIP check performed after EDIT_COSTDATABASE already passed, answered
  // as not-found on purpose — never convert that one to INSUFFICIENT_PERMISSION.
  COST_DATABASE_NOT_FOUND: {
    errorCode: 1119,
    errorMessage: t("The selected cost database could not be found."),
    isDynamic: false,
  },
  // `restoreEstimateVersion`: the acting user's `accounts` row could not be resolved.
  ESTIMATE_VERSION_RESTORE_ACCOUNT_NOT_FOUND: {
    errorCode: 1120,
    errorMessage: t("The account restoring this version could not be found."),
    isDynamic: false,
  },
  // `constructEstimateVersionData`: the project has no default currency configured (the guard also
  // covers a missing project row, which is what its sentence says).
  ESTIMATE_PROJECT_DEFAULT_CURRENCY_NOT_FOUND: {
    errorCode: 1121,
    errorMessage: t("Project not found or has no default currency."),
    isDynamic: false,
  },
  // `upgradeFileVersionController`: the named file version could not be resolved. 2 call sites.
  ESTIMATE_FILE_VERSION_NOT_FOUND: {
    errorCode: 1122,
    errorMessage: t("File version not found."),
    isDynamic: false,
  },
  // 1123 (ESTIMATE_FILE_VERSIONS_NOT_FOUND) is retired: nothing sends it. Do not reuse the number.
  // A `PurchaseUnits` row could not be resolved.
  PURCHASE_UNIT_NOT_FOUND: {
    errorCode: 1124,
    errorMessage: t("Purchase unit not found"),
    isDynamic: false,
  },
  // A `MeasurementUnits` row could not be resolved. Distinct from MEASUREMENT_NOT_FOUND (1113), which
  // is a measurement, not a measurement UNIT.
  MEASUREMENT_UNIT_NOT_FOUND: {
    errorCode: 1125,
    errorMessage: t("Measurement unit not found"),
    isDynamic: false,
  },
  // 1126-1147 is the estimates module's SOMETHING_WENT_WRONG (9000) split, continuing the 11xx block
  // above (estimates' RESOURCE_NOT_FOUND pass produced 1114-1125). Four `noPermissionMsg` sites in
  // charges.service.ts moved to INSUFFICIENT_PERMISSION (7012) instead — the module already paired
  // that exact message with that exact code elsewhere. Eleven sites stay generic on purpose: six
  // literally say "Something went wrong", and five forward a runtime value or a child service's own
  // message.
  //
  // 1126-1129: estimate charges (charges.service.ts).
  // 1126-1147 — the estimates module's SOMETHING_WENT_WRONG split. Every message is copied verbatim
  // from the call site it replaces.
  CHARGE_ALREADY_EXISTS: {
    errorCode: 1126,
    errorMessage: t("Charge already exists."),
    isDynamic: false,
  },
  CHARGE_NOT_FOUND: {
    errorCode: 1127,
    errorMessage: t("Charge not found."),
    isDynamic: false,
  },
  // Split by verb because the two sentences name the blocked action explicitly.
  CHARGE_IN_USE_CANNOT_UPDATE: {
    errorCode: 1128,
    errorMessage: t("Selected charge is already in use hence can't update it!"),
    isDynamic: false,
  },
  CHARGE_IN_USE_CANNOT_DELETE: {
    errorCode: 1129,
    errorMessage: t("Selected charge is already in use hence can't delete it!"),
    isDynamic: false,
  },
  // A unit sync was skipped because the item already carries takeoffs.
  ESTIMATE_ITEM_HAS_TAKEOFFS_UNIT_NOT_SYNCED: {
    errorCode: 1130,
    errorMessage: t("The item has takeoffs attached; its unit was not synced."),
    isDynamic: false,
  },
  // 1131 (ESTIMATE_VERSION_MISMATCH) is retired: nothing sends it. Do not reuse the number.
  ESTIMATE_ONLY_FILES_ALLOWED: {
    errorCode: 1132,
    errorMessage: t("Only files can be added to an estimate."),
    isDynamic: false,
  },
  // 1133-1134: merge estimates are read-through views of their constituents, so item writes are
  // refused. Add and edit keep separate codes because each sentence names its own verb.
  MERGE_ESTIMATE_ITEM_ADD_NOT_ALLOWED: {
    errorCode: 1133,
    errorMessage: t("Items cannot be added directly to a merge estimate. Edit its constituent estimates instead."),
    isDynamic: false,
  },
  MERGE_ESTIMATE_ITEM_EDIT_NOT_ALLOWED: {
    errorCode: 1134,
    errorMessage: t("Items cannot be edited directly on a merge estimate. Edit its constituent estimates instead."),
    isDynamic: false,
  },
  ESTIMATE_ITEM_RELATION_NOT_POSITIVE: {
    errorCode: 1135,
    errorMessage: t("Please enter a positive value for relation"),
    isDynamic: false,
  },
  ESTIMATE_NOTE_NOT_FOUND: {
    errorCode: 1136,
    errorMessage: t("No note exists to delete"),
    isDynamic: false,
  },
  ESTIMATE_NOTE_DELETE_FAILED: {
    errorCode: 1137,
    errorMessage: t("Failed to delete note"),
    isDynamic: false,
  },
  ESTIMATE_COST_INDEX_REQUIRES_COST_DATABASE: {
    errorCode: 1138,
    errorMessage: t("Cost Index can be set only on an estimate that has a cost database linked."),
    isDynamic: false,
  },
  ESTIMATE_NO_COST_DATABASE_LINKED: {
    errorCode: 1139,
    errorMessage: t("This estimate is not linked to a cost database."),
    isDynamic: false,
  },
  ESTIMATE_COST_DATABASE_NO_PUBLISHED_VERSION: {
    errorCode: 1140,
    errorMessage: t("The linked cost database has no published version to move to."),
    isDynamic: false,
  },
  MERGE_ESTIMATE_REQUIRES_CONSTITUENT: {
    errorCode: 1141,
    errorMessage: t("A merge estimate requires at least one constituent estimate."),
    isDynamic: false,
  },
  // The estimate row was written but its file-system entry was not — a partial success, which is why
  // it does not reuse any of the plain create-failure codes.
  ESTIMATE_FILE_SYSTEM_REGISTRATION_FAILED: {
    errorCode: 1142,
    errorMessage: t("The estimate was created but could not be registered in the file system."),
    isDynamic: false,
  },
  ESTIMATE_FILE_CONVERSION_INCOMPLETE: {
    errorCode: 1143,
    errorMessage: t("File conversion is not complete."),
    isDynamic: false,
  },
  // BOQ import: a single row could not be saved. Three of its five sites also forward the child
  // service's own message, so they keep that message and take only the code.
  ESTIMATE_BOQ_ROW_SAVE_FAILED: {
    errorCode: 1144,
    errorMessage: t("Could not save an imported BOQ row."),
    isDynamic: false,
  },
  ESTIMATE_IMPORT_ROW_NUMBERS_INVALID: {
    errorCode: 1145,
    errorMessage: t("Import rows must have unique, non-empty row numbers."),
    isDynamic: false,
  },
  ESTIMATE_BOQ_ITEM_NOT_FOUND: {
    errorCode: 1146,
    errorMessage: t("This item doesn't exists in the BOQ"),
    isDynamic: false,
  },
  ESTIMATE_ITEM_TYPE_CHANGE_HAS_CODED_ITEM: {
    errorCode: 1147,
    errorMessage: t("Can't change type because this item has a coded item."),
    isDynamic: false,
  },
  // 1303-1321 is the costDatabase module's SOMETHING_WENT_WRONG (9000) / RESOURCE_NOT_FOUND (9003)
  // split, continuing the 13xx cost-database sub-block started by the two codes above. Three of the
  // module's generic sites needed NO new code at all — their sentence already matched an existing
  // entry exactly (INSUFFICIENT_PERMISSION 7012 for the 11 `noPermissionMsg` sites,
  // PROJECT_NOT_FOUND for "Project not found", PURCHASE_UNIT_NOT_FOUND 1124 for
  // "Purchase unit not found"). Five sites stay generic on purpose: four literally say
  // "Something went wrong" and one interpolates a runtime count.
  // 1303-1321 — the costDatabase module's generic-code split. Every message below is copied
  // verbatim from the call site it replaces, so the split changes no user-facing text. The one
  // exception is COST_DATABASE_ITEM_NOT_FOUND: two of its three sites said "Item not found!" and
  // the third "Item not found"; the majority wording wins, punctuation being non-material.
  COST_DATABASE_NO_VERSIONS: {
    errorCode: 1303,
    errorMessage: t("This cost database has no versions yet."),
    isDynamic: false,
  },
  COST_DATABASE_DEFAULT_COST_INDEX_INVALID: {
    errorCode: 1304,
    errorMessage: t("Default Cost Index must be a positive number."),
    isDynamic: false,
  },
  COST_DATABASE_NAME_REQUIRED: {
    errorCode: 1305,
    errorMessage: t("Cost database name is required."),
    isDynamic: false,
  },
  COST_DATABASE_DESCRIPTION_REQUIRED: {
    errorCode: 1306,
    errorMessage: t("Cost database description is required."),
    isDynamic: false,
  },
  COST_DATABASE_CURRENCY_NOT_FOUND: {
    errorCode: 1307,
    errorMessage: t("Selected currency not found"),
    isDynamic: false,
  },
  // The portal currency row resolved, but the requested currency code is absent from it.
  COST_DATABASE_CURRENCY_SYMBOL_NOT_FOUND: {
    errorCode: 1308,
    errorMessage: t("Selected currency symbol not found"),
    isDynamic: false,
  },
  COST_DATABASE_CREATE_FAILED: {
    errorCode: 1309,
    errorMessage: t("Cost database could not be created."),
    isDynamic: false,
  },
  // Bulk delete: the id list was empty, or at least one id did not resolve. Deliberately
  // undifferentiated — the caller learns only that the set did not fully resolve.
  COST_DATABASES_NOT_FOUND: {
    errorCode: 1310,
    errorMessage: t("One or more cost databases could not be found."),
    isDynamic: false,
  },
  // A `CostDatabaseVersions` row (or its `versionData.costDBversionId`) could not be resolved.
  // 9 call sites, which before this split were spread across BOTH generic codes while shipping one
  // identical sentence. Distinct from COST_DATABASE_NOT_FOUND (1119), which names the database.
  COST_DATABASE_VERSION_NOT_FOUND: {
    errorCode: 1311,
    errorMessage: t("Cost database version not found"),
    isDynamic: false,
  },
  COST_DATABASE_ITEM_CODE_EXISTS: {
    errorCode: 1312,
    errorMessage: t(
      "An item with the same code exists in this cost database. Please use a different code for the item",
    ),
    isDynamic: false,
  },
  // A `CostDatabaseItems` row could not be resolved. Deliberately NOT ESTIMATE_ITEM_NOT_FOUND (1114)
  // even though that code's message is the same sentence — 1114 names an estimate item, and the
  // frontend must be able to tell the two resources apart.
  COST_DATABASE_ITEM_NOT_FOUND: {
    errorCode: 1313,
    errorMessage: t("Item not found!"),
    isDynamic: false,
  },
  // The purchase unit resolved but carries no `defaultMeasurementUnit`. Distinct from
  // PURCHASE_UNIT_NOT_FOUND (1124), where the unit row itself is missing.
  PURCHASE_UNIT_NO_MEASUREMENT_UNIT: {
    errorCode: 1314,
    errorMessage: t("Purchase unit has no measurement unit"),
    isDynamic: false,
  },
  COST_DATABASE_ITEM_CODE_DESCRIPTION_REQUIRED: {
    errorCode: 1315,
    errorMessage: t("Please enter the code and description for the item"),
    isDynamic: false,
  },
  COST_DATABASE_ITEM_RATE_NEGATIVE: {
    errorCode: 1316,
    errorMessage: t("Please enter a non negative value for rate"),
    isDynamic: false,
  },
  COST_DATABASE_ITEM_ID_INVALID: {
    errorCode: 1317,
    errorMessage: t("Invalid cost database item id."),
    isDynamic: false,
  },
  COST_DATABASE_VARIABLE_RATE_RULES_INVALID: {
    errorCode: 1318,
    errorMessage: t("Invalid variable rate rules"),
    isDynamic: false,
  },
  COST_DATABASE_RANGE_UNIT_NOT_FOUND: {
    errorCode: 1319,
    errorMessage: t("Range unit not found"),
    isDynamic: false,
  },
  // Optimistic-concurrency loss while discarding a draft version — retryable, unlike every other
  // code in this block.
  COST_DATABASE_DRAFT_VERSION_CHANGED: {
    errorCode: 1320,
    errorMessage: t("Draft version changed while it was being discarded. Please retry."),
    isDynamic: false,
  },
  // Deliberately not ESTIMATE_VERSION_TO_RESTORE_NOT_FOUND (1118) — that one names an estimate
  // version and words its sentence differently.
  COST_DATABASE_VERSION_TO_RESTORE_NOT_FOUND: {
    errorCode: 1321,
    errorMessage: t("Version to restore not found"),
    isDynamic: false,
  },
  // 1416-1417 completes the units module's split: the CREATE-time name-clash guards, whose
  // sentences differ from the EDIT-time ones at 1408/1409.
  MEASUREMENT_UNIT_NAME_EXISTS_ON_CREATE: {
    errorCode: 1416,
    errorMessage: t("Measurement Unit cannot be created as another unit with this name already exists."),
    isDynamic: false,
  },
  PURCHASE_UNIT_NAME_EXISTS_ON_CREATE: {
    errorCode: 1417,
    errorMessage: t("Purchase Unit cannot be created as another unit with this name already exists."),
    isDynamic: false,
  },
  // 1148-1149 came off INSUFFICIENT_PERMISSION (7012), not off a generic code: a merge estimate
  // refusing a write is a STATE rule, not a permission failure, so 7012 was the wrong code for them.
  MERGE_ESTIMATE_OPERATION_NOT_ALLOWED: {
    errorCode: 1148,
    errorMessage: t("This operation is not allowed on a merge estimate."),
    isDynamic: false,
  },
  MERGE_ESTIMATE_EMPTY_NOT_ALLOWED: {
    errorCode: 1149,
    errorMessage: t("Empty merged estimate is not allowed!"),
    isDynamic: false,
  },
  // 1150-1151 came off NO_CHANGES_APPLIED (9005).
  ESTIMATE_BOQ_ITEM_ALREADY_EXISTS: {
    errorCode: 1150,
    errorMessage: t("The item already exists in the BOQ"),
    isDynamic: false,
  },
  ESTIMATE_DOCUMENT_UNCHANGED: {
    errorCode: 1151,
    errorMessage: t("No changes made to the document"),
    isDynamic: false,
  },
  // 1152-1157 came off VALIDATION_FAILED (9004).
  ESTIMATE_IDS_REQUIRED: {
    errorCode: 1152,
    errorMessage: t("estimateId and estimateParentId are required"),
    isDynamic: false,
  },
  ESTIMATE_NAME_REQUIRED: {
    errorCode: 1153,
    errorMessage: t("Estimate name is required."),
    isDynamic: false,
  },
  ESTIMATE_COST_INDEX_NOT_POSITIVE: {
    errorCode: 1154,
    errorMessage: t("Cost Index must be a positive number."),
    isDynamic: false,
  },
  ESTIMATE_COST_INDEX_REQUIRED_WITH_COST_DATABASE: {
    errorCode: 1155,
    errorMessage: t("Cost Index is required when a cost database is linked."),
    isDynamic: false,
  },
  ESTIMATE_SPACE_REQUIRED: {
    errorCode: 1156,
    errorMessage: t("Space is required to create an estimate."),
    isDynamic: false,
  },
  ESTIMATE_PROJECT_REQUIRED: {
    errorCode: 1157,
    errorMessage: t("Project is required to create an estimate."),
    isDynamic: false,
  },
  // Entries for codes that had NONE — they would have resolved to a blank message once the
  // inline sentences are removed. Each message is taken from that code's own call site.
  ESTIMATE_NOTHING_TO_UPDATE: {
    errorCode: 1203,
    errorMessage: t("There is nothing to update on this item."),
    isDynamic: false,
  },
  ESTIMATE_MEASUREMENT_UNIT_INVALID: {
    errorCode: 1202,
    errorMessage: t("Measurement unit is not fully configured"),
    isDynamic: false,
  },
  // Splitting ESTIMATE_ITEM_INVALID (1204) and ESTIMATE_NOTHING_TO_UPDATE (1203): both were
  // umbrella codes whose call sites shipped several unrelated sentences under one code. Each fact
  // now has its own code, so removing the inline messages cannot collapse them.
  ESTIMATE_ITEM_DESCRIPTION_REQUIRED: {
    errorCode: 1158,
    errorMessage: t("Please enter the description for the item"),
    isDynamic: false,
  },
  ESTIMATE_ITEM_UNIT_AND_RATE_REQUIRED: {
    errorCode: 1159,
    errorMessage: t("Please specify the unit and rate for the item"),
    isDynamic: false,
  },
  ESTIMATE_BOQ_PARENT_ITEM_NOT_FOUND: {
    errorCode: 1160,
    errorMessage: t("Parent item not found in the BOQ."),
    isDynamic: false,
  },
  ESTIMATE_ITEM_PURCHASE_UNIT_NOT_FOUND: {
    errorCode: 1161,
    errorMessage: t("The selected purchase unit could not be found."),
    isDynamic: false,
  },
  ESTIMATE_VERSION_NO_COST_DATABASE_VERSION: {
    errorCode: 1162,
    errorMessage: t("This estimate version is not linked to a Cost Database version."),
    isDynamic: false,
  },
  ESTIMATE_ITEM_RANGE_OUTSIDE_VARIABLE_RATE: {
    errorCode: 1163,
    errorMessage: t("The range value is outside the item's defined variable-rate bands."),
    isDynamic: false,
  },
  ESTIMATE_ITEM_NOT_IN_COST_DATABASE: {
    errorCode: 1164,
    errorMessage: t("The selected item could not be found in the linked Cost Database."),
    isDynamic: false,
  },
  ESTIMATE_COST_DATABASE_ALREADY_LATEST: {
    errorCode: 1165,
    errorMessage: t("The cost database is already on its latest version."),
    isDynamic: false,
  },
  ESTIMATE_NOTHING_TO_UPDATE_ON_ESTIMATE: {
    errorCode: 1166,
    errorMessage: t("There is nothing to update on this estimate."),
    isDynamic: false,
  },
  ESTIMATE_COST_DATABASE_UPGRADE_TAKEOFF_WIZARD_ERROR: {
    errorCode: 1201,
    errorMessage: t("This estimate's takeoff wizard needs a linked cost database before it can be upgraded."),
    isDynamic: false,
  },
  DEFAULT_COST_DATABASE_DELETE: {
    errorCode: 1301,
    errorMessage: t("One or more of the selected cost databases has no creator on record."),
    isDynamic: false,
  },
  // 1167-1169: the merge-estimate constituent validator. It used to accumulate a MESSAGE string
  // across three checks and forward a child's message on a fourth; it now accumulates a CODE.
  MERGE_ESTIMATE_SELECT_ESTIMATES_ONLY: {
    errorCode: 1167,
    errorMessage: t("Please select only estimates to merge."),
    isDynamic: false,
  },
  MERGE_ESTIMATE_SELECT_NAMED_VERSION: {
    errorCode: 1168,
    errorMessage: t("Please select named estimates version to merge."),
    isDynamic: false,
  },
  MERGE_ESTIMATE_SELECT_STANDARD_ONLY: {
    errorCode: 1169,
    errorMessage: t("Please select standard estimates to merge."),
    isDynamic: false,
  },
  // 1170 arrived from staging, which added a fourth constituent guard alongside the three above:
  // a version whose `amount` is absent or NaN would otherwise contribute NaN to the merge total and
  // silently produce a merge estimate with no usable amount.
  MERGE_ESTIMATE_CONSTITUENT_NO_AMOUNT: {
    errorCode: 1170,
    errorMessage: t("A selected estimate has no total amount and cannot be merged."),
    isDynamic: false,
  },
  // One estimate named twice would count its amount and its items twice. Sent by mergeConstituentEstimates to a caller
  // that skips the route schema, which refuses the same request first.
  // ⚠️ KNOWN BUG, open, found 2026-11-02: 1171 is also ESTIMATE_ITEM_MEASUREMENT_UNIT_NOT_EDITABLE (near the top of
  // this file), and a code is turned into text through one entry per number (errorCodeToEntry in ../errorCodeMapper.ts,
  // where the later entry wins), so that refusal of /estimates/updateEstimateItem (a new measurement unit on an item a
  // takeoff feeds) reads "Select each estimate to merge only once." on web, mobile and MCP. Fix: give this entry the
  // next free number in this file (1172; the entry is new in the permission release, so no deployed client reads it as
  // 1171), update the notes that name it as 1171 (packages/server/estimates/estimates.service.ts at the refusal in
  // mergeConstituentEstimates, packages/server/scripts/releaseChecks/prod-count-p6.js, packages/server/translateKeys.js
  // and the web's ReviewFlowTransitions/checkedReviewers.ts), and add a helpers test that no two entries share a
  // number.
  MERGE_ESTIMATE_DUPLICATE_CONSTITUENT: {
    errorCode: 1171,
    errorMessage: t("Select each estimate to merge only once."),
    isDynamic: false,
  },
  COST_DATABASE_IMPORT_PARTIAL_FAILURE: {
    errorCode: 1322,
    errorMessage: t("{{count}} item(s) could not be imported"),
    isDynamic: true,
    dynamicParams: ["count"],
  },
  // 1418-1424: the estimate item-validation and Excel-import helpers, which both took a sentence. 1159 keeps its own "specify" wording; the call site says "enter", hence the separate code.
  ESTIMATE_ITEM_RATE_POSITIVE_REQUIRED: {
    errorCode: 1418,
    errorMessage: t("Please enter a positive value for rate"),
    isDynamic: false,
  },
  ESTIMATE_ITEM_PURCHASE_UNIT_REQUIRED: {
    errorCode: 1419,
    errorMessage: t("Please enter a purchase unit"),
    isDynamic: false,
  },
  ESTIMATE_ITEM_UNIT_AND_RATE_ENTER_REQUIRED: {
    errorCode: 1420,
    errorMessage: t("Please enter the unit and rate for the item"),
    isDynamic: false,
  },
  ESTIMATE_IMPORT_INVALID_ANCESTORS: {
    errorCode: 1421,
    errorMessage: t("Invalid Anscestor(s) of row {{row}}"),
    isDynamic: true,
    dynamicParams: ["row"],
  },
  ESTIMATE_IMPORT_CODED_ITEM_MISSING: {
    errorCode: 1422,
    errorMessage: t("Coded item of row {{row}} does not exist "),
    isDynamic: true,
    dynamicParams: ["row"],
  },
  ESTIMATE_TAKEOFF_WIZARD_NO_RESULTS: {
    errorCode: 1423,
    errorMessage: t("The Model to Takeoff Wizard could not extract any results for this model."),
    isDynamic: false,
  },
  ESTIMATE_TAKEOFF_WIZARD_NO_COST_DATABASE: {
    errorCode: 1424,
    errorMessage: t(
      "The Model to Takeoff Wizard requires the Estimate to be linked to a Cost Database, which is currently missing.",
    ),
    isDynamic: false,
  },
  UNIT_ITEM_LINKED_TO_TAKEOFF: {
    errorCode: 1426,
    errorMessage: t("This item is linked to a Takeoff, so its Measurement Unit and conversion can't be changed."),
    isDynamic: false,
  },
  ESTIMATE_REFERENCE_VARIABLE_REQUIRED: {
    errorCode: 1503,
    errorMessage: t("Every reference needs a variable."),
    isDynamic: false,
  },
  ESTIMATE_NO_CURRENCY_TO_KEEP: {
    errorCode: 1504,
    errorMessage: t("This estimate has no currency to keep."),
    isDynamic: false,
  },
  ESTIMATE_ITEM_CONVERSION_RATE_REQUIRED: {
    errorCode: 1505,
    errorMessage: t("Please enter the conversion rate for the new measurement unit"),
    isDynamic: false,
  },
} satisfies Record<string, ErrorCodeMapperEntry>;
